import React, { useEffect, useRef, useState } from 'react';
import { FaSearch, FaSpinner } from 'react-icons/fa';
import { catalogNodeService } from '../../../../services/apiService';

export default function PartNameSearch({
  value,
  onChange,
  onSelect,
  vehiclePath = {},
  placeholder = 'Search part name — e.g. Brake Pads, AC Compressor',
}) {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  useEffect(() => {
    const q = String(query || '').trim();
    if (q.length < 2) {
      setResults([]);
      return undefined;
    }

    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        const res = await catalogNodeService.searchParts(q, {
          make: vehiclePath.make,
          model: vehiclePath.model,
          limit: 12,
        });
        setResults(res?.data || []);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query, vehiclePath.make, vehiclePath.model]);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const pick = (item) => {
    const name = item.partCategory;
    setQuery(name);
    onChange?.(name);
    onSelect?.(item);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative">
      <label className="block text-xs font-semibold text-slate-600 mb-1">Part name *</label>
      <div className="relative">
        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            onChange?.(e.target.value);
            setOpen(true);
          }}
          onFocus={() => results.length && setOpen(true)}
          placeholder={placeholder}
          className="w-full pl-9 pr-9 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
        />
        {loading && (
          <FaSpinner className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin text-sm" />
        )}
      </div>

      {open && results.length > 0 && (
        <ul className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg">
          {results.map((item) => (
            <li key={`${item.partCategory}-${item.source}`}>
              <button
                type="button"
                onClick={() => pick(item)}
                className="w-full text-left px-3 py-2.5 hover:bg-emerald-50 border-b border-slate-100 last:border-0"
              >
                <span className="block text-sm font-medium text-slate-900">{item.partCategory}</span>
                <span className="text-[10px] text-slate-500">
                  {item.subSystem} · Profile {item.profile}
                  {item.vehicleHint ? ` · ${item.vehicleHint}` : ''}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
